import { supabase } from "@/integrations/supabase/client";
import { UserProfile, UserRole } from "@/types";
import { UserRole as UserRoleEnum } from "@/types/enums";
import { EmployeeStatus, ResidencyType, WorkerType } from '@/contexts/employees/EmployeesContext';
import { User as SupabaseUser } from "@supabase/supabase-js";
import { toast } from "sonner";
import { profileService } from '@/services/profileService';

export const useProfileFetching = (logout: () => Promise<void>) => {
  const fetchUserProfile = async (supabaseUser: SupabaseUser) => {
    try {
      // Use the get_profile_by_id RPC function to avoid RLS recursion
      // @ts-expect-error - Supabase type inference issue
      const { data: initialRpcData, error } = await supabase.rpc('get_profile_by_id', {
        user_id: supabaseUser.id
      } as any);
      let rpcData = initialRpcData as any;
      
      if (error || !rpcData || rpcData.length === 0) {
        // For newly invited users, try calling ensure-profile to create the profile
        console.log('Profile not found, attempting to create it via ensure-profile...');
        
        try {
          // ✅ Use the centralized profile service
          const result = await profileService.ensureProfile();
          
          if (result.success) {
            console.log('Profile created successfully, retrying fetch...');
            // Small delay to ensure profile is created
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Retry the profile fetch
            // @ts-expect-error - Supabase type inference issue
            const { data: retryRpcData, error: retryError } = await supabase.rpc('get_profile_by_id', {
              user_id: supabaseUser.id
            } as any);
            
            if (!retryError && retryRpcData && (retryRpcData as any).length > 0) {
              // Continue with profile processing below
              rpcData = retryRpcData;
              console.log('Profile fetch successful after creation');
            } else {
              console.error('Profile creation succeeded but fetch still failed');
              toast.error('Profile setup incomplete. Please refresh the page or contact support.');
              return null; // Don't logout, just return null to allow retry
            }
          } else {
            console.error('Profile creation failed:', result.error);
            // Log more details for debugging
            if (result.error) {
                console.error('Error message:', result.error);
              }
              toast.error('Profile setup failed. Please refresh the page or contact support.');
              return null; // Don't logout, just return null to allow retry
            }
        } catch (error) {
          console.error('Error creating profile:', error);
          // For invited users who might have temporary network issues, don't logout immediately
          toast.error('Profile setup failed. Please refresh the page and try again.');
          return null; // Don't logout, allow retry
        }
      }
      
      
      // Since we have the profile basics from the RPC, now get company information
      const userRole = ((rpcData as any)[0].role as UserRole) || UserRoleEnum.EMPLOYEE;
      let companyId = null;
      let companyName = null;
      
      try {
        // Use the get_user_company_id function to get company ID
        // @ts-expect-error - Supabase type inference issue
        const { data: companyIdData } = await (supabase.rpc('get_user_company_id', {
          user_id: supabaseUser.id
        } as any) as any);
        
        if (companyIdData) {
          companyId = companyIdData;
          
          // If we got a company ID, fetch the company name
          if (companyId) {
            const { data: companyData } = await supabase
              .from('companies')
              .select('name')
              .eq('id', companyId)
              .single();
              
            if (companyData) {
              companyName = (companyData as any).name;
            }
          }
        }
      } catch {
        // Error handled silently
      }
      
      // Now fetch additional profile data - this works because we have our own profile in RLS
      const { data: profileDetails, error: profileDetailsError } = await supabase
        .from('profiles')
        .select('first_name, last_name, department, position, shift, avatar, is_active, created_at, updated_at, worker_type')
        .eq('id', supabaseUser.id)
        .single();
        
      // Create the profile with core data plus any additional details
      const userProfile: UserProfile = {
        id: (rpcData as any)[0].id,
        email: (rpcData as any)[0].email,
        name: (rpcData as any)[0].email.split('@')[0], // Default fallback
        firstName: '',
        lastName: '',
        role: userRole,
        department: '',
        position: '',
        shift: '',
        avatar: '',
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        workerType: WorkerType.FULL_TIME,
        companyId: companyId,
        companyName: companyName,
        status: EmployeeStatus.ACTIVE,
        residency: ResidencyType.RESIDENT,
      };
      
      // Update with profile details if available
      if (!profileDetailsError && profileDetails) {
        userProfile.firstName = (profileDetails as any).first_name || '';
        userProfile.lastName = (profileDetails as any).last_name || '';
        userProfile.name = `${(profileDetails as any).first_name || ''} ${(profileDetails as any).last_name || ''}`.trim() || userProfile.name;
        // Handle department as string
        userProfile.department = (profileDetails as any).department || '';
        userProfile.position = (profileDetails as any).position || '';
        userProfile.shift = (profileDetails as any).shift || '';
        userProfile.avatar = (profileDetails as any).avatar || '';
        userProfile.isActive = (profileDetails as any).is_active !== false;
        userProfile.createdAt = (profileDetails as any).created_at || userProfile.createdAt;
        userProfile.updatedAt = (profileDetails as any).updated_at || userProfile.updatedAt;
        userProfile.workerType = (profileDetails as any).worker_type || userProfile.workerType;
      }
      
      return userProfile;
    } catch {
      toast.error('An unexpected error occurred. Please try again.');
      await logout();
      return null;
    }
  };
  
  const fetchNotifications = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
        
      if (error) {
        return [];
      }
      
      return data || [];
    } catch {
      return [];
    }
  };

  return { fetchUserProfile, fetchNotifications };
};
